import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'
import ModelCard from 'src/components/GenerationModel/ModelCard'
import { useMyInfo } from 'src/services/apollo/auth'
import type {
  GenerationModelDetailFragment,
  GenerationModelPreviewFragment,
  GenerationModelPreviewWithTrainingTasksFragment,
} from 'src/services/apollo/generated/graphql'
import {
  usePagedListGenerationModels,
  usePagedListGenerationModelsWithTrainingTask,
} from 'src/services/apollo/generation-model'
import { OrderOption } from 'src/services/helpers/orderOptionParams'
import type { BaseCursorPagingRef } from '../CursorPaging/BaseCursorPaging'
import BaseCursorPaging from '../CursorPaging/BaseCursorPaging'
import type { OnCursorChange } from '../CursorPaging/CursorPagingButton'
import Empty from '../Empty'
import { Preloader } from '../Preloader/Preloader'
import { UIDialogResponsive } from '../ui/Modal'
import ModelDetail from './GenerationModelDetail/ModelDetail'

export default function UserModelList(props: {
  userId: string
  filters?: Record<string, string>
  editing?: boolean
  selectedItems?: string[]
  onClickModel?: (model: GenerationModelPreviewFragment) => void
  cursorState?: { before?: string; after?: string }
  onCursorChange?: OnCursorChange
}) {
  const { t } = useTranslation(['market'])
  const { before, after } = props.cursorState ?? {}
  const pagingRef = useRef<BaseCursorPagingRef>(null)

  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [activeModel, setActiveModel] =
    useState<GenerationModelDetailFragment>()

  const { data: userInfo } = useMyInfo()

  function onClickModel(model: GenerationModelPreviewFragment) {
    if (!props.editing) {
      setActiveModel(model)
      setIsDetailOpen(true)
    } else {
      props.onClickModel?.(model)
    }
  }

  const isMyProfilePage = userInfo?.id === props.userId

  const content = (
    <BaseCursorPaging
      useSWR={
        isMyProfilePage
          ? usePagedListGenerationModelsWithTrainingTask
          : usePagedListGenerationModels
      }
      getConnection={r => r}
      extraArgs={{
        authorId: props.userId,
        orderBy: match(props.filters)
          .with({ sort: OrderOption.oldest }, () => 'createdAt')
          .with({ sort: OrderOption.mostLiked }, () => '-markInfo.likedCount')
          .with({ sort: OrderOption.mostUsed }, () => '-markInfo.refCount')
          .otherwise(() => '-createdAt'),
      }}
      isPosSeq
      pageSize={20}
      pagingRef={pagingRef}
      renderItem={(
        model:
          | GenerationModelPreviewFragment
          | GenerationModelPreviewWithTrainingTasksFragment,
      ) => {
        return (
          <ModelCard
            key={model.id}
            model={model}
            link
            selected={
              props.editing
                ? props.selectedItems?.includes(model.id)
                : undefined
            }
            onClick={(ev, model) => {
              ev.preventDefault()
              onClickModel(model)
            }}
            onUpdate={pagingRef?.current?.mutate}
          />
        )
      }}
      empty={<Empty description={t('market:user-models.no-content')} />}
      before={before}
      after={after}
      onCursorChange={props.onCursorChange}
    />
  )

  return (
    <>
      {content}

      <UIDialogResponsive
        dismissable
        open={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        className="mmd:w-[calc(100vw-64px)] max-w-screen-mlg overflow-y-auto"
      >
        {activeModel ? <ModelDetail model={activeModel} /> : <Preloader />}
      </UIDialogResponsive>
    </>
  )
}
